import { LockClosedIcon } from "@heroicons/react/20/solid";
import { useSearchParams } from "react-router";
import { ExpensesTab } from "@/components/finance/ExpensesTab";
import { OverviewTab, type FinanceTab } from "@/components/finance/OverviewTab";
import { PaymentsTab } from "@/components/finance/PaymentsTab";
import { RulesTab } from "@/components/finance/RulesTab";
import { TeamTab } from "@/components/finance/TeamTab";
import { CurrencyProvider, CurrencyToggles } from "@/components/finance/common";
import { Breadcrumbs, Tabs } from "@/components/page";
import { ErrorState, PageSpinner } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { useProject } from "@/lib/project";
import { useFinance } from "@/lib/queries";
import { NotFoundPage } from "./NotFoundPage";

const tabs: { id: FinanceTab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "payments", label: "Payments" },
  { id: "expenses", label: "Expenses" },
  { id: "team", label: "Team pay" },
  { id: "rules", label: "Settings" },
];

function FinanceContent() {
  const { pid, bundle } = useProject();
  const fin = useFinance(pid);
  const [params, setParams] = useSearchParams();
  const tab = tabs.some((t) => t.id === params.get("tab")) ? (params.get("tab") as FinanceTab) : "overview";
  const open = (t: FinanceTab) => setParams({ tab: t }, { replace: true });

  return (
    <>
      <Breadcrumbs items={[{ label: bundle.project.name, to: `/p/${pid}` }, { label: "Finance" }]} />
      <div className="mx-auto max-w-5xl px-6 pt-8 pb-24 md:px-12">
        <h1 className="text-[32px] leading-tight font-bold">Finance</h1>
        <p className="mt-1 flex items-start gap-1.5 text-sm text-muted">
          <LockClosedIcon className="mt-[3px] size-3.5 shrink-0" />
          Contract, client payments, expenses and team pay. Only project owners and admins can see this page.
        </p>
        {fin.data && fin.data.currencies.length > 0 && <CurrencyToggles fin={fin.data} />}
        <div className="mt-6 mb-8">
          <Tabs value={tab} onChange={open} tabs={tabs} />
        </div>
        {fin.isPending && <PageSpinner />}
        {fin.error && <ErrorState message={errorMessage(fin.error)} onRetry={() => fin.refetch()} />}
        {fin.data && (
          <CurrencyProvider fin={fin.data}>
            {tab === "overview" && <OverviewTab fin={fin.data} onOpen={open} />}
            {tab === "payments" && <PaymentsTab fin={fin.data} />}
            {tab === "expenses" && <ExpensesTab fin={fin.data} />}
            {tab === "team" && <TeamTab fin={fin.data} />}
            {tab === "rules" && <RulesTab fin={fin.data} />}
          </CurrencyProvider>
        )}
      </div>
    </>
  );
}

export function FinancePage() {
  const { isOwner } = useProject();
  if (!isOwner) return <NotFoundPage message="Only project owners and admins can see the finance page." />;
  return <FinanceContent />;
}
